import React from 'react';
import { Box, Typography, IconButton, Tooltip, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import dayjs from 'dayjs';
import { MdOutlineModeEdit } from 'react-icons/md';
import { Coupon } from 'apis/agency/coupons/types';
import { ActionsCellProps, CouponColumnsProps, BusinessUsageCellProps } from './types';
import { confirmDialog, titleCase } from '@/utils/helperFunctions';
import { MdOutlineArchive } from 'react-icons/md';
import { MdOutlineUnarchive } from 'react-icons/md';
import { useArchiveCoupon } from '@/apis/agency/coupons/hook';

function ActionsCell({ coupon, onEdit }: Readonly<ActionsCellProps>) {
  const { mutate: archiveCoupon, isPending: isArchiving } = useArchiveCoupon();
  const theme = useTheme();
  const handleArchiveCoupon = async () => {
    const isActive = coupon.status === 'active';
    const result = await confirmDialog({
      title: isActive ? 'Archive Coupon' : 'Unarchive Coupon',
      text: `Are you sure you want to ${isActive ? 'archive' : 'unarchive'} this item?`,
      icon: 'warning',
      iconColor: '#EE9518',
      confirmButtonColor: '#EE9518',
      cancelButtonColor: 'transparent',
      confirmButtonText: isActive ? 'Archive Coupon' : 'Unarchive Coupon',
      cancelButtonText: 'Cancel'
    });
    if (result.isConfirmed)
      archiveCoupon({ id: coupon.id, status: coupon.status === 'active' ? 'archived' : 'active' });
  };
  return (
    <Box display='flex' gap={1}>
      <IconButton size='medium' sx={{ color: '#777777' }} onClick={() => onEdit(coupon.id)}>
        <MdOutlineModeEdit />
      </IconButton>
      <IconButton
        size='medium'
        disabled={isArchiving}
        sx={{ color: coupon.status !== 'active' ? theme.palette.primary.main : '#777777' }}
        onClick={() => handleArchiveCoupon()}
      >
        {coupon.status === 'active' ? <MdOutlineArchive /> : <MdOutlineUnarchive />}
      </IconButton>
    </Box>
  );
}

function BusinessUsageCell({ coupon }: Readonly<BusinessUsageCellProps>) {
  const businesses = coupon.businesses;

  const usageText = businesses.length > 1 ? `${businesses.length} businesses using..` : `${businesses[0]?.name ?? ''}`;

  if (businesses?.length > 1)
    return (
      <Box display='flex' alignItems='center' gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant='body2'>{usageText}</Typography>
        <Tooltip
          title={
            <Box>
              {businesses.map((business) => (
                <Typography key={business.id} variant='body2' sx={{ mb: 0.5 }}>
                  • {business.name}
                </Typography>
              ))}
            </Box>
          }
          placement='top'
          arrow
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'rgba(0, 0, 0, 0.9)',
                color: 'white',
                maxWidth: 300,
                fontSize: '14px',
                '& .MuiTooltip-arrow': {
                  color: 'rgba(0, 0, 0, 0.9)'
                }
              }
            }
          }}
        >
          <IconButton size='small' sx={{ p: 0.5 }}>
            <IoIosInformationCircleOutline size={16} color='#666' />
          </IconButton>
        </Tooltip>
      </Box>
    );

  return <Typography variant='body2'>{usageText}</Typography>;
}

export function getCouponColumns({ onDelete, onEdit }: CouponColumnsProps): ColumnDef<Coupon>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 100,
      cell: ({ row }) => (
        <Typography variant='body2' fontWeight={500}>
          {row.original.name}
        </Typography>
      )
    },
    {
      accessorKey: 'code',
      header: 'Code',
      size: 100,
      cell: ({ row }) => (
        <Typography variant='body2' fontWeight={500}>
          {row.original.code}
        </Typography>
      )
    },
    {
      accessorKey: 'Type',
      header: 'Type',
      size: 100,
      cell: ({ row }) => {
        const couponType = row.original.coupon_type;
        return (
          <Typography
            fontWeight={500}
            color={couponType === 'business_specific' ? '#077F13' : 'primary'}
            variant='body2'
          >
            {titleCase(couponType)}
          </Typography>
        );
      }
    },
    {
      accessorKey: 'businesses',
      header: 'Business/Usage',
      cell: ({ row }) => <BusinessUsageCell coupon={row.original} />
    },
    {
      accessorKey: 'discount',
      header: 'Discount',
      cell: ({ row }) => {
        const coupon = row.original;
        const discountText =
          coupon.discount_type === 'percentage'
            ? `${coupon.discount_value}%${coupon.total_amount ? ` (up to $${coupon.total_amount})` : ''}`
            : `$${coupon.discount_value}`;
        return <Typography variant='body2'>{discountText} off</Typography>;
      }
    },
    {
      accessorKey: 'validity_period',
      header: 'validity period',
      cell: ({ row }) => {
        const validityPeriod = row.original.validity_period;
        return <Typography variant='body2'>{validityPeriod} months</Typography>;
      }
    },
    {
      accessorKey: 'apply_by_date',
      header: 'apply by date',
      cell: ({ row }) => (
        <Typography variant='body2' noWrap>
          {dayjs(row.original.starts_at).format('MMM DD, YYYY')} /{' '}
          {dayjs(row.original.expires_at).format('MMM DD, YYYY')}
        </Typography>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      cell: ({ row }) => {
        const status = row.original.status;
        const isActive = status === 'active';
        return (
          <Typography
            sx={{
              px: 1.5,
              py: 0.5,
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 6,
              bgcolor: isActive ? '#DBFCDE' : '#ECECEC',
              color: isActive ? '#077F13' : '#777777',
              display: 'inline-block'
            }}
          >
            {titleCase(status)}
          </Typography>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <ActionsCell coupon={row.original} onDelete={onDelete} onEdit={onEdit} />
    }
  ];
}
